/**
 * X-Frame Hypertrophy Program - Complete Workout Templates
 * 
 * Source: J3U X-Frame Hypertrophy Program by John Jewett
 * 
 * This file contains all workout templates, progression schemes,
 * and program structure for the X-Frame Companion App.
 */

// ============================================
// TYPES & INTERFACES
// ============================================

export type WorkoutType = 
  | 'chest_push' 
  | 'pull_a' 
  | 'legs' 
  | 'shoulder_push' 
  | 'pull_b' 
  | 'ham_legs';

export type MuscleGroup = 
  | 'side_delt' 
  | 'rear_delt' 
  | 'front_delt'
  | 'chest' 
  | 'triceps' 
  | 'abs'
  | 'lats'
  | 'upper_back'
  | 'biceps'
  | 'forearms'
  | 'quads'
  | 'hamstrings'
  | 'adductors'
  | 'calves';

export type MovementBias = 
  | 'lat_bias'
  | 'upper_back_bias'
  | 'side_delt'
  | 'rear_delt'
  | 'front_delt'
  | 'upper_chest'
  | 'mid_chest'
  | 'chest_fly'
  | 'tricep_long_head'
  | 'tricep_lateral'
  | 'bicep_long_head'
  | 'bicep_short_head'
  | 'hammer_curl'
  | 'quad_compound'
  | 'quad_isolation'
  | 'hamstring_curl'
  | 'hamstring_hinge'
  | 'adductor'
  | 'calf_straight_leg'
  | 'abs';

export type VolumeTier = 1 | 2 | 3;
export type Phase = 1 | 2;
export type Week = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

export interface ExerciseSlot {
  id: string;                    // e.g., 'A1', 'B1', 'C1'
  name: string;                  // Default exercise name
  bias: MovementBias;            // Movement bias for filtering alternatives
  muscleGroup: MuscleGroup;      // Primary muscle targeted
  sets: number;                  // Number of working sets
  reps: string;                  // Rep range, e.g., '8-12', '10-15'
  alternatives: string[];        // Alternative exercise names
  notes?: string;                // Special instructions (e.g., 'alternating set')
  isCompound: boolean;           // For RIR differentiation
}

export interface WorkoutTemplate {
  type: WorkoutType;
  displayName: string;
  muscleVolume: Record<MuscleGroup, number>;  // Sets per muscle group
  exercises: ExerciseSlot[];
}

export interface RIRTarget {
  compound: number;
  isolation: number;
}

export interface WeekProgression {
  week: Week;
  rir: RIRTarget;
  intensifier?: 'rest_pause' | 'drop_set';
  intensifierTargets?: string[];  // Exercise IDs that get intensifiers
  isDeload: boolean;
  deloadInstructions?: string;
}

// ============================================
// VOLUME TIER SCHEDULE
// ============================================

export const VOLUME_TIER_SCHEDULES: Record<VolumeTier, WorkoutType[]> = {
  1: [
    'chest_push',  // Day 1
    'pull_a',      // Day 2
    null,          // Day 3 - Off
    'legs',        // Day 4
    null,          // Day 5 - Off
    'shoulder_push', // Day 6
    'pull_b',      // Day 7
    null,          // Day 8 - Off
    'ham_legs',    // Day 9
    null,          // Day 10 - Off
  ] as WorkoutType[],
  
  2: [
    'chest_push',    // Day 1
    'pull_a',        // Day 2
    'legs',          // Day 3
    null,            // Day 4 - Off
    'shoulder_push', // Day 5
    'pull_b',        // Day 6
    'ham_legs',      // Day 7
    null,            // Day 8 - Off
  ] as WorkoutType[],
  
  3: [
    'chest_push',    // Day 1
    'pull_a',        // Day 2
    'legs',          // Day 3
    'shoulder_push', // Day 4
    'pull_b',        // Day 5
    'ham_legs',      // Day 6
    null,            // Day 7 - Off
  ] as WorkoutType[]
};

// ============================================
// PHASE 1 PROGRESSION SCHEME
// ============================================

export const PHASE_1_PROGRESSION: WeekProgression[] = [
  {
    week: 1,
    rir: { compound: 2, isolation: 2 },
    isDeload: false
  },
  {
    week: 2,
    rir: { compound: 2, isolation: 1 },
    isDeload: false
  },
  {
    week: 3,
    rir: { compound: 1, isolation: 0 },
    isDeload: false
  },
  {
    week: 4,
    rir: { compound: 1, isolation: 0 },
    isDeload: false
  },
  {
    week: 5,
    rir: { compound: 1, isolation: 0 },
    isDeload: false
  },
  {
    week: 6,
    rir: { compound: 1, isolation: 0 },
    intensifier: 'rest_pause',
    intensifierTargets: ['last_set_all'],  // Add rest-pause to final set of each exercise
    isDeload: false
  },
  {
    week: 7,
    rir: { compound: 1, isolation: 0 },
    intensifier: 'rest_pause',
    intensifierTargets: ['last_set_all'],
    isDeload: false
  },
  {
    week: 8,
    rir: { compound: 3, isolation: 3 },
    isDeload: true,
    deloadInstructions: 'Reduce set volume by 50%. Use same loads at RIR 3-4. Add extra rest day. If joint stress is high, stay in higher rep ranges.'
  }
];

// ============================================
// PHASE 2 PROGRESSION SCHEME
// ============================================

export const PHASE_2_PROGRESSION: WeekProgression[] = [
  {
    week: 1,
    rir: { compound: 2, isolation: 1 },
    isDeload: false
  },
  {
    week: 2,
    rir: { compound: 1, isolation: 0 },
    isDeload: false
  },
  {
    week: 3,
    rir: { compound: 1, isolation: 0 },
    isDeload: false
  },
  {
    week: 4,
    rir: { compound: 1, isolation: 0 },
    isDeload: false
  },
  {
    week: 5,
    rir: { compound: 1, isolation: 0 },
    isDeload: false
  },
  {
    week: 6,
    rir: { compound: 1, isolation: 0 },
    intensifier: 'drop_set',
    intensifierTargets: ['lateral_raise', 'leg_extension', 'lat_row'],
    isDeload: false
  },
  {
    week: 7,
    rir: { compound: 1, isolation: 0 },
    intensifier: 'drop_set',
    intensifierTargets: ['lateral_raise', 'leg_extension', 'lat_row'],
    isDeload: false
  },
  {
    week: 8,
    rir: { compound: 3, isolation: 3 },
    isDeload: true,
    deloadInstructions: 'Reduce set volume by 50%. Use same loads at RIR 3-4. Add extra rest day. If joint stress is high, stay in higher rep ranges.'
  }
];

// ============================================
// PHASE 1 WORKOUT TEMPLATES
// ============================================

export const PHASE_1_TEMPLATES: Record<WorkoutType, WorkoutTemplate> = {
  chest_push: {
    type: 'chest_push',
    displayName: 'Chest Focused Push',
    muscleVolume: {
      side_delt: 4,
      rear_delt: 3,
      front_delt: 0,
      chest: 8,
      triceps: 4,
      abs: 4,
      lats: 0,
      upper_back: 0,
      biceps: 0,
      forearms: 0,
      quads: 0,
      hamstrings: 0,
      adductors: 0,
      calves: 0
    },
    exercises: [
      {
        id: 'A1',
        name: 'Lateral Raise Machine',
        bias: 'side_delt',
        muscleGroup: 'side_delt',
        sets: 4,
        reps: '10-15',
        alternatives: ['DB Lateral Raise', 'Cable Lateral Raise'],
        isCompound: false
      },
      {
        id: 'B1',
        name: 'Flat DB Press',
        bias: 'mid_chest',
        muscleGroup: 'chest',
        sets: 3,
        reps: '8-12',
        alternatives: ['Smith Machine Flat Press', 'Flat Machine Press'],
        isCompound: true
      },
      {
        id: 'C1',
        name: 'Incline Hammer Strength Chest Press',
        bias: 'upper_chest',
        muscleGroup: 'chest',
        sets: 3,
        reps: '8-12',
        alternatives: ['Incline DB Press', 'Incline Smith Press', 'Incline Machine Press'],
        isCompound: true
      },
      {
        id: 'D1',
        name: 'Pec Deck Fly',
        bias: 'chest_fly',
        muscleGroup: 'chest',
        sets: 2,
        reps: '10-15',
        alternatives: ['Cable Fly', 'Machine Fly'],
        notes: 'alternating set with D2',
        isCompound: false
      },
      {
        id: 'D2',
        name: 'Reverse Pec Deck Fly',
        bias: 'rear_delt',
        muscleGroup: 'rear_delt',
        sets: 3,
        reps: '12-20',
        alternatives: ['Cable Reverse Fly', 'Rear Delt Machine'],
        notes: 'alternating set with D1',
        isCompound: false
      },
      {
        id: 'E1',
        name: 'Shoulder Width Overhand Pushdown',
        bias: 'tricep_lateral',
        muscleGroup: 'triceps',
        sets: 4,
        reps: '12-20',
        alternatives: ['Rope Pushdown', 'V-Bar Pushdown'],
        notes: 'alternating set with E2',
        isCompound: false
      },
      {
        id: 'E2',
        name: 'Abdominal Crunch',
        bias: 'abs',
        muscleGroup: 'abs',
        sets: 4,
        reps: '10-15',
        alternatives: ['Rope Crunch', 'Hip Raise', 'GHR Crunch'],
        notes: 'alternating set with E1',
        isCompound: false
      }
    ]
  },

  pull_a: {
    type: 'pull_a',
    displayName: 'Pull A',
    muscleVolume: {
      side_delt: 0,
      rear_delt: 0,
      front_delt: 0,
      chest: 0,
      triceps: 0,
      abs: 0,
      lats: 6,
      upper_back: 4,
      biceps: 6,
      forearms: 2,
      quads: 0,
      hamstrings: 0,
      adductors: 0,
      calves: 0
    },
    exercises: [
      {
        id: 'A1',
        name: 'Lat Bias Pulldown',
        bias: 'lat_bias',
        muscleGroup: 'lats',
        sets: 3,
        reps: '8-12',
        alternatives: ['Single Arm Cable Pulldown', 'Nautilus High Row', 'Hammer Strength High Row'],
        isCompound: true
      },
      {
        id: 'B1',
        name: 'Upper Back Bias Row',
        bias: 'upper_back_bias',
        muscleGroup: 'upper_back',
        sets: 2,
        reps: '8-12',
        alternatives: ['T-Bar Row', 'DB Incline Bench Row'],
        isCompound: true
      },
      {
        id: 'C1',
        name: 'Lat Bias Row',
        bias: 'lat_bias',
        muscleGroup: 'lats',
        sets: 3,
        reps: '10-15',
        alternatives: ['Single Arm Cable Row', 'Prime Row', 'Hammer Strength Row', 'SA Landmine Row'],
        isCompound: true
      },
      {
        id: 'D1',
        name: 'Upper Back Bias Row',
        bias: 'upper_back_bias',
        muscleGroup: 'upper_back',
        sets: 2,
        reps: '12-20',
        alternatives: ['Hammer Strength Iso Row', 'Bent DB Row', 'Steeper Angle Row'],
        isCompound: false
      },
      {
        id: 'E1',
        name: 'DB Preacher Curl',
        bias: 'bicep_short_head',
        muscleGroup: 'biceps',
        sets: 3,
        reps: '10-15',
        alternatives: ['Machine Preacher Curl', 'Cable Preacher Curl'],
        isCompound: false
      },
      {
        id: 'F1',
        name: 'Hammer Cable Curl',
        bias: 'hammer_curl',
        muscleGroup: 'biceps',
        sets: 3,
        reps: '10-15',
        alternatives: ['DB Hammer Curl', 'Rope Hammer Curl'],
        isCompound: false
      },
      {
        id: 'G1',
        name: 'DB Wrist Extension',
        bias: 'hammer_curl',
        muscleGroup: 'forearms',
        sets: 2,
        reps: '12-15',
        alternatives: ['Barbell Wrist Curl', 'Behind Back Wrist Curl'],
        isCompound: false
      }
    ]
  },

  legs: {
    type: 'legs',
    displayName: 'Legs (Quad Focused)',
    muscleVolume: {
      side_delt: 0,
      rear_delt: 0,
      front_delt: 0,
      chest: 0,
      triceps: 0,
      abs: 0,
      lats: 0,
      upper_back: 0,
      biceps: 0,
      forearms: 0,
      quads: 8,
      hamstrings: 4,
      adductors: 2,
      calves: 4
    },
    exercises: [
      {
        id: 'A1',
        name: 'Seated Hamstring Curl',
        bias: 'hamstring_curl',
        muscleGroup: 'hamstrings',
        sets: 4,
        reps: '8-12',
        alternatives: ['Lying Hamstring Curl', 'Prone DB Curl'],
        isCompound: false
      },
      {
        id: 'B1',
        name: 'Squat of Choice',
        bias: 'quad_compound',
        muscleGroup: 'quads',
        sets: 2,
        reps: '8-12',
        alternatives: ['SSB Squat', 'Hack Squat', 'Rogers Squat', 'Barbell Back Squat'],
        isCompound: true
      },
      {
        id: 'C1',
        name: 'Leg Press of Choice',
        bias: 'quad_compound',
        muscleGroup: 'quads',
        sets: 3,
        reps: '12-20',
        alternatives: ['45 Degree Leg Press', 'Cybex Leg Press', 'Horizontal Leg Press'],
        isCompound: true
      },
      {
        id: 'D1',
        name: 'Adductor Machine',
        bias: 'adductor',
        muscleGroup: 'adductors',
        sets: 2,
        reps: '10-15',
        alternatives: ['Cable Adduction', 'Copenhagen Plank'],
        isCompound: false
      },
      {
        id: 'E1',
        name: 'Quad Extension',
        bias: 'quad_isolation',
        muscleGroup: 'quads',
        sets: 3,
        reps: '10-15',
        alternatives: ['Leg Extension Machine', 'Single Leg Extension'],
        isCompound: false
      },
      {
        id: 'F1',
        name: 'Straight Leg Calf Raise',
        bias: 'calf_straight_leg',
        muscleGroup: 'calves',
        sets: 4,
        reps: '12-16',
        alternatives: ['Donkey Calf Raise', 'Standing Calf Raise'],
        isCompound: false
      }
    ]
  },

  shoulder_push: {
    type: 'shoulder_push',
    displayName: 'Shoulder Focused Push',
    muscleVolume: {
      side_delt: 4,
      rear_delt: 3,
      front_delt: 3,
      chest: 6,
      triceps: 4,
      abs: 4,
      lats: 0,
      upper_back: 0,
      biceps: 0,
      forearms: 0,
      quads: 0,
      hamstrings: 0,
      adductors: 0,
      calves: 0
    },
    exercises: [
      {
        id: 'A1',
        name: 'Machine Lateral Raise',
        bias: 'side_delt',
        muscleGroup: 'side_delt',
        sets: 4,
        reps: '10-15',
        alternatives: ['Cable Lateral Raise', 'DB Lateral Raise'],
        isCompound: false
      },
      {
        id: 'B1',
        name: 'Shoulder Press Machine of Choice',
        bias: 'front_delt',
        muscleGroup: 'front_delt',
        sets: 3,
        reps: '8-12',
        alternatives: ['DB Shoulder Press', 'BB Military Press', 'Smith Shoulder Press'],
        isCompound: true
      },
      {
        id: 'C1',
        name: 'Close Grip Press',
        bias: 'mid_chest',
        muscleGroup: 'chest',
        sets: 3,
        reps: '8-12',
        alternatives: ['Smith Close Grip Press', 'Close Grip Machine Press'],
        isCompound: true
      },
      {
        id: 'D1',
        name: 'Reverse Cable Fly',
        bias: 'rear_delt',
        muscleGroup: 'rear_delt',
        sets: 3,
        reps: '10-15',
        alternatives: ['Reverse Pec Deck', 'Rear Delt Machine'],
        notes: 'alternating set with D2',
        isCompound: false
      },
      {
        id: 'D2',
        name: 'Chest Cable Fly',
        bias: 'chest_fly',
        muscleGroup: 'chest',
        sets: 3,
        reps: '10-15',
        alternatives: ['Pec Deck', 'Machine Fly'],
        notes: 'alternating set with D1',
        isCompound: false
      },
      {
        id: 'E1',
        name: 'Overhead Tricep Extension',
        bias: 'tricep_long_head',
        muscleGroup: 'triceps',
        sets: 4,
        reps: '10-15',
        alternatives: ['DB Overhead Extension', 'Cable Overhead Extension'],
        notes: 'alternating set with E2',
        isCompound: false
      },
      {
        id: 'E2',
        name: 'Abdominal Crunch',
        bias: 'abs',
        muscleGroup: 'abs',
        sets: 4,
        reps: '10-15',
        alternatives: ['Rope Crunch', 'Hip Raise', 'GHR Crunch'],
        notes: 'alternating set with E1',
        isCompound: false
      }
    ]
  },

  pull_b: {
    type: 'pull_b',
    displayName: 'Pull B',
    muscleVolume: {
      side_delt: 0,
      rear_delt: 0,
      front_delt: 0,
      chest: 0,
      triceps: 0,
      abs: 0,
      lats: 4,
      upper_back: 6,
      biceps: 6,
      forearms: 2,
      quads: 0,
      hamstrings: 0,
      adductors: 0,
      calves: 0
    },
    exercises: [
      {
        id: 'A1',
        name: 'Upper Back Bias Pulldown',
        bias: 'upper_back_bias',
        muscleGroup: 'upper_back',
        sets: 3,
        reps: '10-15',
        alternatives: ['Machine Pulldown', 'Cable Pulldown'],
        isCompound: true
      },
      {
        id: 'B1',
        name: 'Upper Back Bias Row',
        bias: 'upper_back_bias',
        muscleGroup: 'upper_back',
        sets: 3,
        reps: '8-12',
        alternatives: ['Chest Supported T-Bar Row', 'Horizontal Row Machine', 'DB Row'],
        isCompound: true
      },
      {
        id: 'C1',
        name: 'Lat Row of Choice',
        bias: 'lat_bias',
        muscleGroup: 'lats',
        sets: 2,
        reps: '8-12',
        alternatives: ['Seated Cable Row', 'Machine Row'],
        isCompound: true
      },
      {
        id: 'D1',
        name: 'Lat Pulldown of Choice',
        bias: 'lat_bias',
        muscleGroup: 'lats',
        sets: 2,
        reps: '12-20',
        alternatives: ['Single Arm Pulldown', 'Bilateral Cable Pulldown', 'Machine Pulldown'],
        isCompound: false
      },
      {
        id: 'E1',
        name: 'Incline Cable Curl',
        bias: 'bicep_long_head',
        muscleGroup: 'biceps',
        sets: 3,
        reps: '10-15',
        alternatives: ['Incline DB Curl', 'Machine Curl'],
        isCompound: false
      },
      {
        id: 'F1',
        name: 'Preacher DB Hammer Curl',
        bias: 'hammer_curl',
        muscleGroup: 'biceps',
        sets: 3,
        reps: '10-15',
        alternatives: ['Cable Hammer Curl', 'Machine Hammer Curl'],
        isCompound: false
      },
      {
        id: 'G1',
        name: 'DB Wrist Extension',
        bias: 'hammer_curl',
        muscleGroup: 'forearms',
        sets: 2,
        reps: '12-15',
        alternatives: ['Barbell Wrist Curl'],
        isCompound: false
      }
    ]
  },

  ham_legs: {
    type: 'ham_legs',
    displayName: 'Hamstring Focused Legs',
    muscleVolume: {
      side_delt: 0,
      rear_delt: 0,
      front_delt: 0,
      chest: 0,
      triceps: 0,
      abs: 0,
      lats: 0,
      upper_back: 0,
      biceps: 0,
      forearms: 0,
      quads: 3,
      hamstrings: 6,
      adductors: 3,
      calves: 4
    },
    exercises: [
      {
        id: 'A1',
        name: 'Lying Hamstring Curl',
        bias: 'hamstring_curl',
        muscleGroup: 'hamstrings',
        sets: 4,
        reps: '8-12',
        alternatives: ['Seated Hamstring Curl', 'Prone Hamstring Curl'],
        isCompound: false
      },
      {
        id: 'B1',
        name: 'RDL',
        bias: 'hamstring_hinge',
        muscleGroup: 'hamstrings',
        sets: 2,
        reps: '8-12',
        alternatives: ['Barbell RDL', 'DB RDL', 'Pit Shark RDL'],
        isCompound: true
      },
      {
        id: 'C1',
        name: 'Front Foot Elevated Split Squat',
        bias: 'quad_compound',
        muscleGroup: 'quads',
        sets: 3,
        reps: '10-15',
        alternatives: ['Single Leg Leg Press', 'DB Split Squat', 'Smith Split Squat', 'Hip Press'],
        isCompound: true
      },
      {
        id: 'D1',
        name: 'Adductor Machine',
        bias: 'adductor',
        muscleGroup: 'adductors',
        sets: 3,
        reps: '10-15',
        alternatives: ['Cable Adduction'],
        isCompound: false
      },
      {
        id: 'E1',
        name: 'Straight Leg Calf Raise',
        bias: 'calf_straight_leg',
        muscleGroup: 'calves',
        sets: 4,
        reps: '6-10',
        alternatives: ['Donkey Calf Raise', 'Standing Calf Raise'],
        isCompound: false
      }
    ]
  }
};

// ============================================
// PHASE 2 WORKOUT TEMPLATES
// (20% volume increase for quads, lats, delts)
// ============================================

export const PHASE_2_TEMPLATES: Record<WorkoutType, WorkoutTemplate> = {
  chest_push: {
    ...PHASE_1_TEMPLATES.chest_push,
    muscleVolume: {
      ...PHASE_1_TEMPLATES.chest_push.muscleVolume,
      side_delt: 4,  // +1 from rear delt adjustment
      rear_delt: 4,  // +1 set (20% increase rounded)
    },
    exercises: [
      ...PHASE_1_TEMPLATES.chest_push.exercises.slice(0, 4),
      {
        ...PHASE_1_TEMPLATES.chest_push.exercises[4], // D2 Reverse Pec Deck
        sets: 4  // Increased from 3 to 4
      },
      ...PHASE_1_TEMPLATES.chest_push.exercises.slice(5)
    ]
  },

  pull_a: {
    ...PHASE_1_TEMPLATES.pull_a,
    muscleVolume: {
      ...PHASE_1_TEMPLATES.pull_a.muscleVolume,
      lats: 7,       // +1 set (20% increase)
      upper_back: 5, // +1 set
    },
    exercises: [
      PHASE_1_TEMPLATES.pull_a.exercises[0],
      {
        ...PHASE_1_TEMPLATES.pull_a.exercises[1], // B1 Upper Back Bias Row
        sets: 3  // Increased from 2 to 3
      },
      ...PHASE_1_TEMPLATES.pull_a.exercises.slice(2)
    ]
  },

  legs: {
    ...PHASE_1_TEMPLATES.legs,
    muscleVolume: {
      ...PHASE_1_TEMPLATES.legs.muscleVolume,
      quads: 8,  // Maintained (already high, increase via intensity)
    },
    exercises: PHASE_1_TEMPLATES.legs.exercises  // Same structure
  },

  shoulder_push: {
    ...PHASE_1_TEMPLATES.shoulder_push,
    muscleVolume: {
      ...PHASE_1_TEMPLATES.shoulder_push.muscleVolume,
      side_delt: 4,
      rear_delt: 4,  // +1 set
    },
    exercises: [
      ...PHASE_1_TEMPLATES.shoulder_push.exercises.slice(0, 3),
      {
        ...PHASE_1_TEMPLATES.shoulder_push.exercises[3], // D1 Reverse Cable Fly
        sets: 4  // Increased from 3 to 4
      },
      ...PHASE_1_TEMPLATES.shoulder_push.exercises.slice(4)
    ]
  },

  pull_b: {
    ...PHASE_1_TEMPLATES.pull_b,
    muscleVolume: {
      ...PHASE_1_TEMPLATES.pull_b.muscleVolume,
      lats: 5,  // +1 set
    },
    exercises: [
      ...PHASE_1_TEMPLATES.pull_b.exercises.slice(0, 2),
      {
        ...PHASE_1_TEMPLATES.pull_b.exercises[2], // C1 Lat Row
        sets: 3  // Increased from 2 to 3
      },
      ...PHASE_1_TEMPLATES.pull_b.exercises.slice(3)
    ]
  },

  ham_legs: {
    ...PHASE_1_TEMPLATES.ham_legs,
    muscleVolume: {
      ...PHASE_1_TEMPLATES.ham_legs.muscleVolume,
      side_delt: 4,  // Added lateral raise to ham leg day in Phase 2
      quads: 4,      // +1 set
    },
    exercises: [
      {
        id: 'A1',
        name: 'Cable Lateral Raise',
        bias: 'side_delt',
        muscleGroup: 'side_delt',
        sets: 4,
        reps: '12-15',
        alternatives: ['DB Lateral Raise', 'Machine Lateral Raise'],
        isCompound: false
      },
      {
        ...PHASE_1_TEMPLATES.ham_legs.exercises[0],
        id: 'B1'  // Renumbered
      },
      {
        ...PHASE_1_TEMPLATES.ham_legs.exercises[1],
        id: 'C1'
      },
      {
        ...PHASE_1_TEMPLATES.ham_legs.exercises[2],
        id: 'D1',
        sets: 4  // Increased from 3 to 4
      },
      {
        ...PHASE_1_TEMPLATES.ham_legs.exercises[3],
        id: 'E1'
      },
      {
        ...PHASE_1_TEMPLATES.ham_legs.exercises[4],
        id: 'F1'
      }
    ]
  }
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get the appropriate template for a given phase and workout type
 */
export function getWorkoutTemplate(phase: Phase, workoutType: WorkoutType): WorkoutTemplate {
  return phase === 1 
    ? PHASE_1_TEMPLATES[workoutType] 
    : PHASE_2_TEMPLATES[workoutType];
}

/**
 * Get the progression scheme for a given phase and week
 */
export function getWeekProgression(phase: Phase, week: Week): WeekProgression {
  const progressions = phase === 1 ? PHASE_1_PROGRESSION : PHASE_2_PROGRESSION;
  return progressions.find(p => p.week === week) || progressions[0];
}

/**
 * Get the target RIR for an exercise based on phase, week, and exercise type
 */
export function getTargetRIR(phase: Phase, week: Week, isCompound: boolean): number {
  const progression = getWeekProgression(phase, week);
  return isCompound ? progression.rir.compound : progression.rir.isolation;
}

/**
 * Check if a week is a deload week
 */
export function isDeloadWeek(week: Week): boolean {
  return week === 8;
}

/**
 * Get exercises that should have intensifiers applied
 */
export function getIntensifierExercises(phase: Phase, week: Week): string[] {
  const progression = getWeekProgression(phase, week);
  return progression.intensifierTargets || [];
}

/**
 * Calculate total weekly volume for a muscle group based on volume tier
 */
export function getWeeklyMuscleVolume(
  phase: Phase, 
  volumeTier: VolumeTier, 
  muscleGroup: MuscleGroup
): number {
  const templates = phase === 1 ? PHASE_1_TEMPLATES : PHASE_2_TEMPLATES;
  let totalSets = 0;
  
  // Count occurrences of each workout in the tier schedule
  const schedule = VOLUME_TIER_SCHEDULES[volumeTier];
  const workoutCounts: Record<WorkoutType, number> = {} as Record<WorkoutType, number>;
  
  for (const workout of schedule) {
    if (workout) {
      workoutCounts[workout] = (workoutCounts[workout] || 0) + 1;
    }
  }
  
  // Sum volume across all workouts
  for (const [workoutType, count] of Object.entries(workoutCounts)) {
    const template = templates[workoutType as WorkoutType];
    totalSets += (template.muscleVolume[muscleGroup] || 0) * count;
  }
  
  // Adjust for 10-day vs 7-day cycle
  const cycleDays = schedule.length;
  const weeklyMultiplier = 7 / cycleDays;
  
  return Math.round(totalSets * weeklyMultiplier);
}

// ============================================
// DAILY WAIST TRAINING PROTOCOL
// ============================================

export const DAILY_WAIST_TRAINING = {
  description: 'Perform daily to develop X-Frame waist control',
  protocol: [
    {
      exercise: 'Abdominal Vacuum',
      sets: 10,
      instruction: 'Blow out all air and suck in abdomen. Hold as long as possible.',
      notes: 'Alternate with abdominal contractions'
    },
    {
      exercise: 'Abdominal Contraction',
      sets: 10,
      instruction: 'Contract rectus abdominis and obliques to cinch waist. Take shallow breaths. Hold 15-20 seconds.',
      notes: 'Practice in front of mirror'
    }
  ],
  restBetweenSets: 'Minimal - goal is continuous alternation',
  tips: [
    'Do not let abdomen stay distended all day',
    'Pick foods that digest well and do not bloat',
    'Pick foods with reasonable volumes'
  ]
};

// ============================================
// REST TIME RECOMMENDATIONS
// ============================================

export const REST_TIMES = {
  compound: {
    min: 120,  // 2 minutes
    max: 180,  // 3 minutes
    default: 150  // 2.5 minutes
  },
  isolation: {
    min: 60,   // 1 minute
    max: 120,  // 2 minutes
    default: 90  // 1.5 minutes
  },
  description: 'Goal is for heart rate to return to baseline for adequate recovery'
};

// ============================================
// BAD DAY AUTOREGULATION PROTOCOL
// ============================================

export const BAD_DAY_PROTOCOL = {
  questionnaire: [
    { question: 'Is your Perceived Recovery very low?', scale: '1-10' },
    { question: 'Was sleep poor last night?', type: 'yes_no' },
    { question: 'Was stress worse today than normal?', type: 'yes_no' },
    { question: 'Were meals missed or fluids off today?', type: 'yes_no' },
    { question: 'Any joint pain or aches?', type: 'yes_no' }
  ],
  rules: [
    {
      condition: 'Yes to 2+ questions',
      action: 'Light session: 12-15 reps, one set per exercise, RIR 2-3'
    },
    {
      condition: 'Yes only to joint pain',
      action: 'Avoid painful movements, keep reps above 15, same volume as normal'
    }
  ]
};

// ============================================
// EXPORTS
// ============================================

export default {
  PHASE_1_TEMPLATES,
  PHASE_2_TEMPLATES,
  PHASE_1_PROGRESSION,
  PHASE_2_PROGRESSION,
  VOLUME_TIER_SCHEDULES,
  DAILY_WAIST_TRAINING,
  REST_TIMES,
  BAD_DAY_PROTOCOL,
  getWorkoutTemplate,
  getWeekProgression,
  getTargetRIR,
  isDeloadWeek,
  getIntensifierExercises,
  getWeeklyMuscleVolume
};
